import { BpSelect } from '../../../../shared/components/forms/select/BpSelect'
import { Controller, useForm } from 'react-hook-form'
import { useAppDispatch, useAppSelector } from '../../../../app/store/hooks/Redux'
import { IAdOverviewFilter } from '../../model/types/AdOverviewFilter.interface'
import {
  selectAdOverviewActiveTab,
  selectAdOverviewFilters,
  selectProject,
  setAdOverviewFilter,
  setProject,
  TAdOverviewTabs,
} from '../../model/adOverviewSlice'
import {
  BpSearchInput,
  IOption,
} from '../../../../shared/components/forms/search-input/BpSearchInput'
import { toTextOptions } from '../../../../shared/lib/helpers/ToTextOptions'
import { FilterDrop } from '../../../../shared/components/cards/filter-drop/FilterDrop'
import moment from 'moment'
import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_RANGE_FORMAT,
} from '../../../../shared/consts/DefaultRangeFormat.const'
import React, { useEffect } from 'react'
import { PolicyOptions } from '../../../../shared/consts/PolicyOptions.const'
import {
  IWithSearchParamsProps,
  withSearchParams,
} from '../../../../shared/lib/hoc/WithSearchParams'
import { Switch } from 'antd'
import { useTranslation } from 'react-i18next'
import { AddProjectButton } from '../../../../shared/components/buttons/add-project-button/AddProjectButton'
import { EditCountriesButton } from '../../../../shared/components/buttons/edit-countries-button/EditCountriesButton'
import { FilterChips } from '../../../../shared/components/utility/filter-chips/FilterChips'
import { DateSelect } from '../../../../shared/components/forms/date-select/DateSelect'
import { useSearchState } from '../../../../lib/hooks/useSearchState'
import { useReversedFilters } from '../../../../lib/hooks/useReversedFilters'
import {
  resetFilterFields,
  useRegisterAppliedFilters,
} from '../../../../lib/context/applied-filters/AppliedFiltersProvider'
import { BpCheckbox } from '../../../../shared/components/forms/checkbox/BpCheckbox'
import clsx from 'clsx'

import styles from './AdOverviewFilters.module.scss'
import {
  GetFilterAdvertiserDomainsApiArg,
  GetFilterAffiliatesApiArg,
  GetFilterCityApiArg,
  useGetFilterAdvertiserDomainsQuery,
  useGetFilterAdvertiserNamesQuery,
  useGetFilterAffiliatesQuery,
  useGetFilterBrowserQuery,
  useGetFilterCityQuery,
  useGetFilterCountryQuery,
  useGetFilterEngineQuery,
  useGetFilterProjectsQuery,
} from '../../../../app/api'
import { ProjectIcon } from '../../../../shared/components/icons/filters/ProjectIcon'
import { CountryIcon } from '../../../../shared/components/icons/filters/CountryIcon'
import { SearchEngineIcon } from '../../../../shared/components/icons/filters/SearchEngineIcon'
import { BrowserIcon } from '../../../../shared/components/icons/filters/BrowserIcon'
import { CityIcon } from '../../../../shared/components/icons/filters/CityIcon'
import { PolicyIcon } from '../../../../shared/components/icons/filters/PolicyIcon'
import { AffiliateIcon } from '../../../../shared/components/icons/filters/AffiliateIcon'
import { AdvertiserIcon } from '../../../../shared/components/icons/filters/AdvertiserIcon'
import { DomainIcon } from '../../../../shared/components/icons/filters/DomainIcon'
import { withSelectedOptions } from '../../../../shared/lib/helpers/WithSelectedOptions'
import { renderCountryOption } from '../../../../shared/components/utility/country-option/CountryOption'
import { restoreAppliedValues } from '../../../../shared/lib/helpers/AppliedValues'

const REVERSED_FILTER_KEYS: Record<string, string> = {
  search: 'search',
  engine: 'engine_id',
  affiliate: 'affiliate_id',
  city: 'city_id',
  browser: 'browser_id',
  policy: 'policy',
  advertiserName: 'advertiser_id',
}

/** Фильтры, которые сбрасывает Clear filters, и их значения по умолчанию. */
const CLEARABLE_FILTERS: Partial<IAdOverviewFilter> = {
  country: '',
  adDomain: '',
  search: '',
  engine: '',
  city: '',
  browser: '',
  policy: '',
  affiliate: '',
  advertiserName: '',
  show_hidden: false,
  group: false,
}

const Filter = ({ searchParams, setSearchParams }: IWithSearchParamsProps<IAdOverviewFilter>) => {
  const { t } = useTranslation()
  const activeTab = useAppSelector<TAdOverviewTabs>(selectAdOverviewActiveTab)
  const filters = useAppSelector<IAdOverviewFilter>(selectAdOverviewFilters)
  const { project, search, dateRange, country, affiliate, advertiserName } = filters
  const { is_demo: isDemo } = useAppSelector(selectProject)
  const dispatch = useAppDispatch()
  const [dropSearch, setDropSearch] = useSearchState({
    affiliate: '',
    advertiserName: '',
    adDomain: '',
  })

  const {
    getValues,
    setValue,
    control,
    formState: { defaultValues },
  } = useForm<IAdOverviewFilter>({
    mode: 'onChange',
    defaultValues: { ...filters, ...searchParams },
  })

  useEffect(() => {
    applyFilter()
  }, [])

  const {
    data: projects,
    isFetching: projectsFetching,
    isLoading: projectsLoading,
  } = useGetFilterProjectsQuery({})

  const {
    data: countries,
    isFetching: countriesFetching,
    isLoading: countriesLoading,
  } = useGetFilterCountryQuery(
    {
      projectId: project ? +project : undefined,
    },
    {
      skip: !project,
    },
  )

  const {
    data: engines,
    isFetching: enginesFetching,
    isLoading: enginesLoading,
  } = useGetFilterEngineQuery({ projectId: project ? +project : undefined }, { skip: !project })

  const {
    data: browsers,
    isFetching: browsersFetching,
    isLoading: browsersLoading,
  } = useGetFilterBrowserQuery()

  const {
    data: advertiserNames,
    isFetching: advertiserNamesFetching,
    isLoading: advertiserNamesLoading,
  } = useGetFilterAdvertiserNamesQuery(
    {
      date: dateRange,
      projectId: +project,
      search: dropSearch.advertiserName,
    },
    { skip: !project },
  )

  const {
    data: cities,
    isFetching: citiesFetching,
    isLoading: citiesLoading,
  } = useGetFilterCityQuery(
    {
      projectId: project ? +project : undefined,
      countryId: country || undefined,
    } as unknown as GetFilterCityApiArg,
    { skip: !project },
  )

  const {
    data: adDomains,
    isFetching: domainsFetching,
    isLoading: domainsLoading,
  } = useGetFilterAdvertiserDomainsQuery(
    {
      projectId: project ? +project : undefined,
      search: dropSearch.adDomain,
      date: dateRange,
      seo: false,
    } as GetFilterAdvertiserDomainsApiArg,
    { skip: !project },
  )

  const {
    data: affiliates,
    isFetching: affiliatesFetching,
    isLoading: affiliatesLoading,
  } = useGetFilterAffiliatesQuery(
    {
      date: dateRange,
      isSeo: false,
      projectId: +project,
      search: dropSearch.affiliate,
    } as GetFilterAffiliatesApiArg,
    { skip: !project },
  )

  const applyFilterWithReversed = (reversed: string[]) => {
    const values = { ...getValues(), reversed_filters: reversed.join(',') }
    dispatch(setAdOverviewFilter(values))
    if (setSearchParams) setSearchParams(values)
  }

  const {
    reversedFilters,
    isReversed,
    isReversedApplied,
    toggleReversedFilter,
    toggleReversedFilterAndApply,
    clearReversedFilter,
    resetReversedFilters,
    restoreReversedFilters,
  } = useReversedFilters({
    keys: REVERSED_FILTER_KEYS,
    applied: filters.reversed_filters,
    fromUrl: searchParams?.reversed_filters,
    onApply: applyFilterWithReversed,
  })

  const applyFilter = () => applyFilterWithReversed(reversedFilters)

  useRegisterAppliedFilters({
    values: filters,
    defaults: CLEARABLE_FILTERS,
    onClear: () => {
      resetFilterFields(setValue, CLEARABLE_FILTERS)
      resetReversedFilters()
      applyFilterWithReversed([])
    },
  })

  return (
    <div>
      <form className={clsx(styles.form)}>
        <div className={clsx(styles.field)}>
          <Controller
            render={({ field: { onChange, ...fieldProps } }) => {
              const defaultValue = defaultValues?.project
                ? defaultValues.project
                : project
                  ? project
                  : projects?.length && projects[0].value

              return (
                <BpSelect
                  {...fieldProps}
                  Icon={ProjectIcon}
                  bottomSection={<AddProjectButton />}
                  defaultValue={defaultValue}
                  disabled={projectsLoading || projectsFetching}
                  fieldSize='lg'
                  loading={projectsLoading || projectsFetching}
                  options={projects}
                  title={t('fields.project')}
                  onChange={(e) => {
                    onChange(e)

                    const option = projects?.find((option) => option.value === e.target.value)

                    if (option) {
                      dispatch(setProject(option))
                    }

                    if (e.target.value !== defaultValue) {
                      setValue('country', '')
                      setValue('adDomain', '')
                      setValue('search', '')
                      setValue('affiliate', '')
                    }
                    applyFilter()
                  }}
                ></BpSelect>
              )
            }}
            control={control}
            name='project'
          />
        </div>
        <div className={clsx(styles.field)}>
          <Controller
            render={({ field: { value, onChange, ...fieldProps } }) => (
              <BpSelect
                {...fieldProps}
                multiple
                Icon={DomainIcon}
                enableChipLoad={false}
                fieldSize='lg'
                loading={domainsLoading || domainsFetching}
                options={toTextOptions(adDomains, value)}
                placeholder={t('fields.domain')}
                value={value?.split(',')}
                onChange={(e) => {
                  onChange(
                    [].slice
                      .call(e.target.selectedOptions)
                      .map((o: IOption) => o.value)
                      .join(','),
                  )
                }}
                onDropClose={applyFilter}
                onSearch={setDropSearch('adDomain')}
              />
            )}
            control={control}
            name='adDomain'
          />
        </div>
        <div className={clsx(styles.field)}>
          <Controller
            render={({ field: { value, onChange, ...fieldProps } }) => (
              <BpSelect
                {...fieldProps}
                multiple
                Icon={CountryIcon}
                bottomSection={<EditCountriesButton project={{ value: project, isDemo }} />}
                disabled={countriesLoading || countriesFetching}
                fieldSize='lg'
                loading={countriesLoading || countriesFetching}
                optionRender={renderCountryOption}
                options={countries}
                title={t('fields.country')}
                value={value?.split(',')}
                onChange={(e) => {
                  const selected = [].slice
                    .call(e.target.selectedOptions)
                    .map((o: IOption) => o.value)
                    .join(',')

                  onChange(selected)
                  if (selected !== value) {
                    setValue('city', '')
                  }
                }}
                onDropClose={applyFilter}
              ></BpSelect>
            )}
            control={control}
            name='country'
          />
        </div>
        <div className={clsx(styles.field)}>
          <Controller
            render={({ field: { onChange, value } }) => {
              return (
                <DateSelect
                  value={[moment(value, DEFAULT_DATE_FORMAT), moment(value, DEFAULT_RANGE_FORMAT)]}
                  onChange={(_, value) => {
                    onChange(`${value[0]} - ${value[1]}`)
                    applyFilter()
                  }}
                />
              )
            }}
            control={control}
            name='dateRange'
          />
        </div>
        <div className={clsx(styles.fieldFull)}>
          <FilterDrop
            values={getValues([
              'search',
              'engine',
              'affiliate',
              'city',
              'browser',
              'policy',
              'show_hidden',
              'group',
              'advertiserName',
            ])}
            onClear={() => {
              setValue('search', '')
              setValue('engine', '')
              setValue('city', '')
              setValue('browser', '')
              setValue('policy', '')
              setValue('show_hidden', false)
              setValue('affiliate', '')
              setValue('advertiserName', '')
              resetReversedFilters()
            }}
            onHide={() => {
              restoreAppliedValues(getValues, setValue, filters)
              restoreReversedFilters()
            }}
            onSubmit={applyFilter}
          >
            <Controller
              render={({ field }) => (
                <BpSearchInput
                  {...field}
                  action={
                    <BpCheckbox
                      checked={isReversed('search')}
                      label={t('fields.exclude')}
                      theme={'grey'}
                      onChange={() => toggleReversedFilter('search')}
                    />
                  }
                  label={t('fields.forKeywordOrAd')}
                  placeholder={t('fields.other.search')}
                ></BpSearchInput>
              )}
              control={control}
              name='search'
            />
            <Controller
              render={({ field: { value, onChange, ...fieldProps } }) => (
                <BpSelect
                  {...fieldProps}
                  multiple
                  action={
                    <BpCheckbox
                      checked={isReversed('engine')}
                      label={t('fields.exclude')}
                      theme={'grey'}
                      onChange={() => toggleReversedFilter('engine')}
                    />
                  }
                  Icon={SearchEngineIcon}
                  disabled={enginesLoading || enginesFetching}
                  label={t('fields.searchEngine')}
                  loading={enginesLoading || enginesFetching}
                  options={engines}
                  placeholder={t('fields.other.selectFromList')}
                  value={value?.split(',')}
                  onChange={(e) => {
                    onChange(
                      [].slice
                        .call(e.target.selectedOptions)
                        .map((o: IOption) => o.value)
                        .join(','),
                    )
                  }}
                />
              )}
              control={control}
              name='engine'
            />
            <Controller
              render={({ field: { value, onChange, ...fieldProps } }) => (
                <BpSelect
                  {...fieldProps}
                  multiple
                  action={
                    <BpCheckbox
                      checked={isReversed('affiliate')}
                      label={t('fields.exclude')}
                      theme={'grey'}
                      onChange={() => toggleReversedFilter('affiliate')}
                    />
                  }
                  Icon={AffiliateIcon}
                  enableChipLoad={false}
                  label={t('fields.affiliate')}
                  loading={affiliatesLoading || affiliatesFetching}
                  options={withSelectedOptions('affiliate', affiliates, value)}
                  placeholder={t('fields.other.selectFromList')}
                  value={value?.split(',')}
                  onChange={(e) => {
                    onChange(
                      [].slice
                        .call(e.target.selectedOptions)
                        .map((o: IOption) => o.value)
                        .join(','),
                    )
                  }}
                  onSearch={setDropSearch('affiliate')}
                ></BpSelect>
              )}
              control={control}
              name='affiliate'
            />
            <Controller
              render={({ field: { value, onChange, ...fieldProps } }) => (
                <BpSelect
                  {...fieldProps}
                  multiple
                  action={
                    <BpCheckbox
                      checked={isReversed('city')}
                      label={t('fields.exclude')}
                      theme={'grey'}
                      onChange={() => toggleReversedFilter('city')}
                    />
                  }
                  Icon={CityIcon}
                  disabled={citiesLoading || citiesFetching}
                  label={t('fields.city')}
                  loading={citiesLoading || citiesFetching}
                  options={cities}
                  placeholder={t('fields.other.selectFromList')}
                  value={value?.split(',')}
                  onChange={(e) => {
                    onChange(
                      [].slice
                        .call(e.target.selectedOptions)
                        .map((o: IOption) => o.value)
                        .join(','),
                    )
                  }}
                />
              )}
              control={control}
              name='city'
            />
            <Controller
              render={({ field: { value, onChange, ...fieldProps } }) => (
                <BpSelect
                  {...fieldProps}
                  multiple
                  action={
                    <BpCheckbox
                      checked={isReversed('browser')}
                      label={t('fields.exclude')}
                      theme={'grey'}
                      onChange={() => toggleReversedFilter('browser')}
                    />
                  }
                  Icon={BrowserIcon}
                  disabled={browsersLoading || browsersFetching}
                  label={t('fields.browser')}
                  loading={browsersLoading || browsersFetching}
                  options={browsers}
                  placeholder={t('fields.other.selectFromList')}
                  value={value?.split(',')}
                  onChange={(e) => {
                    onChange(
                      [].slice
                        .call(e.target.selectedOptions)
                        .map((o: IOption) => o.value)
                        .join(','),
                    )
                  }}
                />
              )}
              control={control}
              name='browser'
            />
            <Controller
              render={({ field: { value, onChange, ...fieldProps } }) => (
                <BpSelect
                  {...fieldProps}
                  hasTranslate
                  multiple
                  action={
                    <BpCheckbox
                      checked={isReversed('policy')}
                      label={t('fields.exclude')}
                      theme={'grey'}
                      onChange={() => toggleReversedFilter('policy')}
                    />
                  }
                  options={[
                    {
                      text: 'Non-Compliant affiliates',
                      value: 'non_compliant_affiliates',
                      hidden: activeTab === 'Affiliates',
                    },
                    { text: 'Coupon sites', value: 'coupon_sites' },
                    {
                      text: 'Ad hijacking',
                      value: 'ad_hijacking',
                    },
                    ...PolicyOptions,
                  ]}
                  Icon={PolicyIcon}
                  label={t('fields.policy')}
                  placeholder={t('fields.other.selectFromList')}
                  value={value?.split(',')}
                  onChange={(e) => {
                    onChange(
                      [].slice
                        .call(e.target.selectedOptions)
                        .map((o: IOption) => o.value)
                        .join(','),
                    )
                  }}
                ></BpSelect>
              )}
              control={control}
              name='policy'
            />
            <Controller
              render={({ field: { value, onChange, ...fieldProps } }) => (
                <BpSelect
                  {...fieldProps}
                  multiple
                  action={
                    <BpCheckbox
                      checked={isReversed('advertiserName')}
                      label={t('fields.exclude')}
                      theme={'grey'}
                      onChange={() => toggleReversedFilter('advertiserName')}
                    />
                  }
                  Icon={AdvertiserIcon}
                  enableChipLoad={false}
                  label={t('fields.advertiser')}
                  loading={advertiserNamesLoading || advertiserNamesFetching}
                  options={withSelectedOptions('advertiser', advertiserNames, value)}
                  placeholder={t('fields.other.selectFromList')}
                  value={value?.split(',')}
                  onChange={(e) => {
                    onChange(
                      [].slice
                        .call(e.target.selectedOptions)
                        .map((o: IOption) => o.value)
                        .join(','),
                    )
                  }}
                  onSearch={setDropSearch('advertiserName')}
                ></BpSelect>
              )}
              control={control}
              name='advertiserName'
            />
            <Controller
              render={({ field }) => (
                <div className={clsx(styles.switchRow)}>
                  <span className={clsx(styles.switchLabel)}>
                    {t('fields.showHiddenAffiliates')}
                  </span>
                  <Switch {...field} defaultChecked={filters.show_hidden} />
                </div>
              )}
              control={control}
              name='show_hidden'
            />
            <Controller
              render={({ field }) => (
                <div className={clsx(styles.switchRow)}>
                  <span className={clsx(styles.switchLabel)}>{t('fields.showUniqueAds')}</span>
                  <Switch {...field} defaultChecked={filters.group} />
                </div>
              )}
              control={control}
              name='group'
            />
          </FilterDrop>
        </div>
      </form>
      <div className={clsx(styles.chipsWrapper)}>
        <FilterChips
          applyCallBack={() => {
            resetReversedFilters()
            applyFilterWithReversed([])
          }}
          clearCallBack={(field: any) => {
            setValue(field, '')
            applyFilterWithReversed(clearReversedFilter(field))
          }}
          items={[
            {
              title: t('fields.forKeywordOrAd'),
              field: 'search',
              reversible: true,
              reversed: isReversedApplied('search'),
              value: search,
            },
            {
              title: t('fields.searchEngine'),
              field: 'engine',
              reversible: true,
              reversed: isReversedApplied('engine'),
              value: filters.engine,
              data: engines,
              type: 'multiselect',
            },
            {
              title: t('fields.affiliate'),
              field: 'affiliate',
              reversible: true,
              reversed: isReversedApplied('affiliate'),
              value: affiliate,
              data: withSelectedOptions('affiliate', affiliates, affiliate),
              type: 'multiselect',
            },
            {
              title: t('fields.city'),
              field: 'city',
              reversible: true,
              reversed: isReversedApplied('city'),
              value: filters.city,
              data: cities,
              type: 'multiselect',
            },
            {
              title: t('fields.browser'),
              field: 'browser',
              reversible: true,
              reversed: isReversedApplied('browser'),
              value: filters.browser,
              data: browsers,
              type: 'multiselect',
            },
            {
              title: t('fields.policy'),
              field: 'policy',
              reversible: true,
              reversed: isReversedApplied('policy'),
              value: filters.policy,
              data: [
                {
                  text: 'Non-Compliant affiliates',
                  value: 'non_compliant_affiliates',
                  hidden: activeTab === 'Affiliates',
                },
                { text: 'Coupon sites', value: 'coupon_sites' },
                {
                  text: 'Ad hijacking',
                  value: 'ad_hijacking',
                },
                ...PolicyOptions,
              ],
              type: 'multiselect',
            },
            {
              title: t('fields.advertiser'),
              field: 'advertiserName',
              reversible: true,
              reversed: isReversedApplied('advertiserName'),
              value: advertiserName,
              data: withSelectedOptions('advertiser', advertiserNames, advertiserName),
              type: 'multiselect',
            },
          ]}
          reverseCallBack={toggleReversedFilterAndApply}
        />
      </div>
    </div>
  )
}

export const AdOverviewFilters = withSearchParams<
  IWithSearchParamsProps<IAdOverviewFilter>,
  IAdOverviewFilter
>(Filter, selectAdOverviewFilters)
